import { plateFont, numFont } from '../../utils/fonts';
import * as React from 'react';
import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  IconButton,
  Collapse,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  Divider,
  Badge,
  Menu,
  CircularProgress,
  Snackbar,
  Alert,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import BoltIcon from '@mui/icons-material/Bolt';
import FilterListIcon from '@mui/icons-material/FilterList';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import EventIcon from '@mui/icons-material/Event';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import PersonIcon from '@mui/icons-material/Person';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import ReceiptIcon from '@mui/icons-material/Receipt';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import TodayIcon from '@mui/icons-material/Today';
import DateRangeIcon from '@mui/icons-material/DateRange';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CancelIcon from '@mui/icons-material/Cancel';
import MergeIcon from '@mui/icons-material/MergeType';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import { withTranslation } from 'react-i18next';
import axios from 'axios';
import AddMaintenanceDialog from './AddMaintenanceDialog';
import DeleteMaintenanceDialog from './DeleteMaintenanceDialog';
import CancelMaintenanceDialog from './CancelMaintenanceDialog';
import OemServiceDialog from './oem/OemServiceDialog';
import AIWorkOrderDialog from './ai/AIWorkOrderDialog';
import QuoteMachineDialog from '../quote-machine/QuoteMachineDialog';
import { useNavigate } from 'react-router-dom';
import { formatDate, formatDateOnly } from '../../utils';
import StatusBadge from '../shared/StatusBadge';
import { usePaginatedList } from '../../hooks/usePaginatedList';
import PaginationControls from '../shared/PaginationControls';
import {
  useListPreferences,
  SearchInput,
  FilterChip,
  dateStringToDate,
  dateToDateString,
  isValidDateString,
  sanitizeDateString,
} from '../shared/ListFilters';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { fi } from 'date-fns/locale';
import DataTable from '../shared/DataTable';
import { extractListFromResponse } from '../../utils/apiUtils';
import { useUserStore } from '../../store';

import { can, isSimplifiedUi } from '../../utils/access';
import { customerLabel } from '../../utils/customer';
const PARTS_ORDER_CONFIG = {
  not_ordered: { color: '#ef4444', bg: '#fee2e2' },
  ordered: { color: '#d97706', bg: '#fef3c7' },
  arrived: { color: '#059669', bg: '#d1fae5' },
  not_needed: { color: '#64748b', bg: '#f1f5f9' },
};

function PartsOrderBadge({ status, t }) {
  if (!status) return null;
  const config = PARTS_ORDER_CONFIG[status];
  if (!config) return null;
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '5px',
        padding: '2px 8px',
        borderRadius: '20px',
        fontSize: '11px',
        fontWeight: 500,
        backgroundColor: config.bg,
        color: config.color,
        fontFamily: "'Plus Jakarta Sans', sans-serif",
        whiteSpace: 'nowrap',
      }}
    >
      <Box component="span" sx={{ width: 5, height: 5, borderRadius: '50%', backgroundColor: config.color }} />
      {t(`parts_order.${status}_short`)}
    </Box>
  );
}

// Customer approval of the whole work order or of a quote (Maintenance.confirmation_status,
// the same field the green box on the work order reads).
const CONFIRMATION_CONFIG = {
  pending: { color: '#b45309', bg: '#fef3c7', label: 'confirmation.listPending' },
  confirmed: { color: '#15803d', bg: '#dcfce7', label: 'confirmation.listConfirmed' },
  declined: { color: '#b91c1c', bg: '#fee2e2', label: 'confirmation.listDeclined' },
};
// Same as the dashboard approvals widget: an open request on these is no longer waiting.
const CONFIRMATION_CLOSED_STATES = ['cancelled', 'picked_up'];

function confirmationTooltip(maintenance, t) {
  const isQuote = !!maintenance.confirmation_quote_id;
  if (maintenance.confirmation_status === 'pending') {
    const date = formatDateOnly(maintenance.confirmation_requested_at);
    return t(isQuote ? 'confirmation.tooltipQuoteRequested' : 'confirmation.tooltipRequested', { date });
  }
  const date = formatDateOnly(maintenance.confirmation_responded_at);
  if (maintenance.confirmation_status === 'confirmed') {
    return t(isQuote ? 'confirmation.tooltipQuoteConfirmed' : 'confirmation.tooltipConfirmed', { date });
  }
  return t(isQuote ? 'confirmation.tooltipQuoteDeclined' : 'confirmation.tooltipDeclined', { date });
}

// Extra work requests (lisätyöpyynnöt): the backend reports "pending" while any request is
// unanswered, otherwise the latest answer.
const EXTRA_WORK_CONFIG = {
  pending: { ...CONFIRMATION_CONFIG.pending, label: 'confirmation.listExtraWorkPending' },
  approved: { ...CONFIRMATION_CONFIG.confirmed, label: 'confirmation.listExtraWorkApproved' },
  denied: { ...CONFIRMATION_CONFIG.declined, label: 'confirmation.listExtraWorkDenied' },
};

function extraWorkTooltip(maintenance, t) {
  if (maintenance.extra_work_status === 'pending') {
    return t('confirmation.tooltipExtraWorkRequested', { date: formatDateOnly(maintenance.extra_work_requested_at) });
  }
  const date = formatDateOnly(maintenance.extra_work_responded_at);
  return t(
    maintenance.extra_work_status === 'approved' ? 'confirmation.tooltipExtraWorkApproved' : 'confirmation.tooltipExtraWorkDenied',
    { date },
  );
}

function ConfirmationBadge({ maintenance, t }) {
  const status = maintenance.confirmation_status;
  const config = CONFIRMATION_CONFIG[status];
  if (!config) return null;
  if (status === 'pending' && CONFIRMATION_CLOSED_STATES.includes(maintenance.state)) return null;
  return (
    <ApprovalBadge config={config} tooltip={confirmationTooltip(maintenance, t)} testId={`confirmation-badge-${status}`} t={t} />
  );
}

function ExtraWorkBadge({ maintenance, t }) {
  const status = maintenance.extra_work_status;
  const config = EXTRA_WORK_CONFIG[status];
  if (!config) return null;
  if (status === 'pending' && CONFIRMATION_CLOSED_STATES.includes(maintenance.state)) return null;
  return (
    <ApprovalBadge config={config} tooltip={extraWorkTooltip(maintenance, t)} testId={`extra-work-badge-${status}`} t={t} />
  );
}

function ApprovalBadge({ config, tooltip, testId, t }) {
  return (
    <Tooltip title={tooltip}>
      <Box
        component="span"
        data-testid={testId}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '5px',
          padding: '2px 8px',
          borderRadius: '20px',
          fontSize: '11px',
          fontWeight: 500,
          backgroundColor: config.bg,
          color: config.color,
          fontFamily: "'Plus Jakarta Sans', sans-serif",
          whiteSpace: 'nowrap',
        }}
      >
        <Box component="span" sx={{ width: 5, height: 5, borderRadius: '50%', backgroundColor: config.color }} />
        {t(config.label)}
      </Box>
    </Tooltip>
  );
}

/**
 * One date-range filter row: the three preset chips plus a custom from/to pair.
 * Rendered twice — once for the scheduled date, once for the creation date — so both read
 * identically. Values are "yyyy-MM-dd" strings; the pickers convert at the edges.
 */
function DateRangeFilterRow({ rangeType, from, to, onChange, testIdPrefix, t }) {
  const togglePreset = (preset) => onChange({ rangeType: rangeType === preset ? 'all' : preset });

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }} data-testid={`${testIdPrefix}-filters`}>
      <FilterChip
        icon={<TodayIcon />}
        label={t('Today')}
        active={rangeType === 'today'}
        onClick={() => togglePreset('today')}
        color="primary"
      />
      <FilterChip
        icon={<DateRangeIcon />}
        label={t('This week')}
        active={rangeType === 'this_week'}
        onClick={() => togglePreset('this_week')}
        color="primary"
      />
      <FilterChip
        icon={<DateRangeIcon />}
        label={t('This month')}
        active={rangeType === 'this_month'}
        onClick={() => togglePreset('this_month')}
        color="primary"
      />

      <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={fi}>
        <DatePicker
          label={t('From')}
          value={dateStringToDate(from)}
          onChange={(newValue) => onChange({ from: dateToDateString(newValue), rangeType: 'custom' })}
          format="dd.MM.yyyy"
          slotProps={{ textField: { size: 'small', sx: { width: 160 }, inputProps: { 'data-testid': `${testIdPrefix}-from` } } }}
        />
        <DatePicker
          label={t('To')}
          value={dateStringToDate(to)}
          onChange={(newValue) => onChange({ to: dateToDateString(newValue), rangeType: 'custom' })}
          format="dd.MM.yyyy"
          slotProps={{ textField: { size: 'small', sx: { width: 160 }, inputProps: { 'data-testid': `${testIdPrefix}-to` } } }}
        />
      </LocalizationProvider>
    </Box>
  );
}

const STORAGE_KEY = 'maintenances_list_preferences';
// The quotes-only view keeps its own filters and sort, so quote hunting never disturbs the
// work order list's saved selection (and vice versa).
const QUOTES_STORAGE_KEY = 'quotes_list_preferences';

// All maintenance states. Never filter this list — it is the yardstick for "is the selection
// narrower than everything", which decides whether the state param has to be sent to the server.
const allMaintenanceStates = [
  { value: 'quote', labelKey: 'quote' },
  { value: 'planned', labelKey: 'planned' },
  { value: 'in_progress', labelKey: 'in_progress' },
  { value: 'waiting', labelKey: 'waiting' },
  { value: 'vehicle_in_workshop', labelKey: 'vehicle_in_workshop' },
  { value: 'completed', labelKey: 'completed' },
  { value: 'picked_up', labelKey: 'picked_up' },
  { value: 'cancelled', labelKey: 'cancelled' },
];

// Which states this view lets the user pick from. With separate_quotes_list_enabled the two
// views split the states between them; without it the work order list owns them all.
const getSelectableStates = (quotesView, separateQuotes) => {
  if (quotesView) return allMaintenanceStates.filter((s) => s.value === 'quote');
  if (separateQuotes) return allMaintenanceStates.filter((s) => s.value !== 'quote');
  return allMaintenanceStates;
};

/**
 * The work order list. Mounted twice when separate_quotes_list_enabled is on:
 * variant="maintenances" (every state but quote) and variant="quotes" (only quote).
 */
const MainMaintenancesView = ({ t, variant = 'maintenances' }) => {
  const { currentOrganization } = useUserStore();
  const simplifiedUi = isSimplifiedUi(currentOrganization);
  const quotesView = variant === 'quotes';
  const separateQuotes = !!currentOrganization?.separate_quotes_list_enabled;
  // Org-scope the saved list preferences: the mechanics filter stores employee IDs, which are
  // org-specific and must not carry over when switching organizations.
  const storageKey = `${quotesView ? QUOTES_STORAGE_KEY : STORAGE_KEY}_${currentOrganization?.organization_id || 'none'}`;

  const selectableStates = useMemo(
    () => getSelectableStates(quotesView, separateQuotes),
    [quotesView, separateQuotes]
  );

  // Default selection: everything this view offers except cancelled.
  const defaultStates = useMemo(
    () => selectableStates.map((s) => s.value).filter((v) => v !== 'cancelled'),
    [selectableStates]
  );

  // Memoized because useListPreferences keeps it in useCallback dependency arrays — a fresh
  // object per render would churn resetFilters' identity.
  const defaultPreferences = useMemo(() => ({
    sortField: 'created_at',
    sortDirection: 'desc',
    searchQuery: '',
    filters: {
      states: defaultStates,
      schedulingStatus: 'all',
      customerStatus: 'all',
      invoiceStatus: 'all',
      partsOrderStatus: 'all',
      confirmationStatus: 'all',
      mechanics: [],
      manufacturers: [],
      dateRangeType: 'all',
      dateFrom: '',
      dateTo: '',
      createdDateRangeType: 'all',
      createdFrom: '',
      createdTo: '',
    },
  }), [defaultStates]);
  const [employees, setEmployees] = useState([]);
  const [employeesLoading, setEmployeesLoading] = useState(true);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [oemDialogOpen, setOemDialogOpen] = useState(false);
  const [quoteMachineOpen, setQuoteMachineOpen] = useState(false);
  const [aiDialogOpen, setAiDialogOpen] = useState(false);
  const [aiPreFillData, setAiPreFillData] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [maintenanceToCancel, setMaintenanceToCancel] = useState(null);
  const [selectedMaintenance, setSelectedMaintenance] = useState(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [menuMaintenance, setMenuMaintenance] = useState(null);
  const [selectedMaintenanceIds, setSelectedMaintenanceIds] = useState(new Set());
  const [combineDialogOpen, setCombineDialogOpen] = useState(false);
  const [combineLoading, setCombineLoading] = useState(false);
  const [bulkCancelDialogOpen, setBulkCancelDialogOpen] = useState(false);
  const [bulkCancelLoading, setBulkCancelLoading] = useState(false);
  const [partsOrderMenuAnchor, setPartsOrderMenuAnchor] = useState(null);

  const navigate = useNavigate();

  // Use localStorage-persisted preferences
  const {
    preferences,
    updatePreferences,
    updateFilters,
    resetFilters: resetLocalFilters,
    filtersOpen: filterPanelOpen,
    toggleFiltersOpen,
  } = useListPreferences(storageKey, defaultPreferences);

  // One-time migration: rename waiting_for_parts → waiting in saved preferences
  useEffect(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        const prefs = JSON.parse(stored);
        if (prefs?.filters?.states?.includes('waiting_for_parts')) {
          prefs.filters.states = prefs.filters.states.map(s =>
            s === 'waiting_for_parts' ? 'waiting' : s
          );
          localStorage.setItem(storageKey, JSON.stringify(prefs));
        }
      }
    } catch (e) { /* ignore */ }
  }, [storageKey]);

  // Extract filter values from preferences (with safeguards for invalid localStorage data)
  const rawStates = Array.isArray(preferences.filters.states) ? preferences.filters.states : defaultStates;

  // Drop states this view isn't allowed to show. Needed because saved preferences outlive the
  // separate_quotes_list_enabled setting: an org that turns it on has 'quote' sitting in every
  // user's stored work order list filters. Derive-only on purpose — rewriting localStorage
  // (like the waiting_for_parts rename below) would destroy the user's quote selection for good,
  // and this setting can be turned back off.
  const statesFilter = useMemo(() => {
    const allowed = new Set(selectableStates.map((s) => s.value));
    const kept = rawStates.filter((s) => allowed.has(s));
    // Nothing stripped: hand back the saved value untouched, including a deliberately empty
    // selection. This is what keeps the setting-off path identical to before.
    if (kept.length === rawStates.length) return rawStates;
    // Stripping emptied the selection (only 'quote' was saved) — fall back to this view's default.
    return kept.length > 0 ? kept : defaultStates;
  }, [rawStates, selectableStates, defaultStates]);
  const schedulingStatus = preferences.filters.schedulingStatus || 'all';
  const customerStatus = preferences.filters.customerStatus || 'all';
  // The invoice and parts-order filters are hidden in the quotes view — a quote has neither an
  // invoice nor ordered parts. Neutralize whatever is in saved preferences, or a value left over
  // from the work order list would narrow the quotes list with no visible chip to clear it.
  const invoiceStatus = quotesView ? 'all' : (preferences.filters.invoiceStatus || 'all');
  const partsOrderStatus = quotesView ? 'all' : (preferences.filters.partsOrderStatus || 'all');
  const confirmationStatus = preferences.filters.confirmationStatus || 'all';
  const mechanicsFilter = Array.isArray(preferences.filters.mechanics) ? preferences.filters.mechanics : [];
  const manufacturersFilter = Array.isArray(preferences.filters.manufacturers) ? preferences.filters.manufacturers : [];
  const dateRangeType = preferences.filters.dateRangeType || 'all';
  const dateFrom = sanitizeDateString(preferences.filters.dateFrom);
  const dateTo = sanitizeDateString(preferences.filters.dateTo);
  const createdDateRangeType = preferences.filters.createdDateRangeType || 'all';
  const createdFrom = sanitizeDateString(preferences.filters.createdFrom);
  const createdTo = sanitizeDateString(preferences.filters.createdTo);

  // Load employees
  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const response = await axios.get('/api/employees/');
        const { data } = extractListFromResponse(response);
        setEmployees(data.filter((e) => e.state === 'active'));
      } catch (err) {
        console.error('Error loading employees:', err);
      } finally {
        setEmployeesLoading(false);
      }
    };
    fetchEmployees();
  }, []);

  // Date range helper. customFrom/customTo are "yyyy-MM-dd" strings, passed in rather than
  // closed over so the same helper serves both the scheduled and the created date filter.
  const getDateRange = useCallback((rangeType, customFrom, customTo) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    switch (rangeType) {
      case 'today':
        return { from: today, to: new Date(today.getTime() + 24 * 60 * 60 * 1000 - 1) };
      case 'this_week': {
        const startOfWeek = new Date(today);
        const day = startOfWeek.getDay();
        const diff = startOfWeek.getDate() - day + (day === 0 ? -6 : 1);
        startOfWeek.setDate(diff);
        const endOfWeek = new Date(startOfWeek);
        endOfWeek.setDate(endOfWeek.getDate() + 7);
        endOfWeek.setTime(endOfWeek.getTime() - 1);
        return { from: startOfWeek, to: endOfWeek };
      }
      case 'this_month': {
        const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 1);
        endOfMonth.setTime(endOfMonth.getTime() - 1);
        return { from: startOfMonth, to: endOfMonth };
      }
      case 'custom':
        // Same guard as the shared getDateRange: a stored value that is not a real yyyy-MM-dd
        // would parse to an Invalid Date, and toISOString() below would crash the whole list.
        return {
          from: isValidDateString(customFrom) ? new Date(customFrom) : null,
          to: isValidDateString(customTo) ? new Date(customTo + 'T23:59:59') : null,
        };
      default:
        return { from: null, to: null };
    }
  }, []);

  // Convert the scheduled date range to backend filter params
  const dateFilterParams = useMemo(() => {
    if (dateRangeType === 'all') return {};

    const { from, to } = getDateRange(dateRangeType, dateFrom, dateTo);
    const params = {};
    if (from) params.scheduled_from = from.toISOString();
    if (to) params.scheduled_to = to.toISOString();
    return params;
  }, [dateRangeType, dateFrom, dateTo, getDateRange]);

  // Same, for the creation date range (created_from/created_to on MaintenanceFilter)
  const createdDateFilterParams = useMemo(() => {
    if (createdDateRangeType === 'all') return {};

    const { from, to } = getDateRange(createdDateRangeType, createdFrom, createdTo);
    const params = {};
    if (from) params.created_from = from.toISOString();
    if (to) params.created_to = to.toISOString();
    return params;
  }, [createdDateRangeType, createdFrom, createdTo, getDateRange]);

  // Build server-side filters
  const serverFilters = useMemo(() => {
    const filters = {};

    // State filter (multi-select). Compare against allMaintenanceStates, not the states this
    // view offers: with the quotes list split off, "all selectable states" is still narrower
    // than "all states", so the list must be sent or the server would hand back quotes too.
    if (statesFilter.length > 0 && statesFilter.length < allMaintenanceStates.length) {
      filters.state = statesFilter;
    } else if (statesFilter.length === 0 && selectableStates.length < allMaintenanceStates.length) {
      // Nothing selected in a restricted view: ask for the allowed states so the ones this view
      // must hide can't leak in. An unrestricted view keeps omitting the param (= every state).
      filters.state = selectableStates.map((s) => s.value);
    }

    // Scheduling status
    if (schedulingStatus === 'scheduled') {
      filters.has_schedule = true;
    } else if (schedulingStatus === 'unscheduled') {
      filters.has_schedule = false;
    }

    // Customer status
    if (customerStatus === 'assigned') {
      filters.has_customer = true;
    } else if (customerStatus === 'unassigned') {
      filters.has_customer = false;
    }

    // Invoice status
    if (invoiceStatus === 'has_invoice') {
      filters.has_invoice = true;
    } else if (invoiceStatus === 'no_invoice') {
      filters.has_invoice = false;
    }

    // Parts order status filter
    if (partsOrderStatus !== 'all') {
      filters.parts_order_status = partsOrderStatus;
    }

    // Customer approval filter
    if (confirmationStatus !== 'all') {
      filters.confirmation_status = confirmationStatus;
    }

    // Mechanics filter
    if (mechanicsFilter.length > 0) {
      filters.employees = mechanicsFilter;
    }

    // Manufacturer filter
    if (manufacturersFilter.length > 0) {
      filters.vehicle_manufacturer = manufacturersFilter;
    }

    // Scheduled date range
    if (dateFilterParams.scheduled_from) {
      filters.scheduled_from = dateFilterParams.scheduled_from;
    }
    if (dateFilterParams.scheduled_to) {
      filters.scheduled_to = dateFilterParams.scheduled_to;
    }

    // Created date range
    if (createdDateFilterParams.created_from) {
      filters.created_from = createdDateFilterParams.created_from;
    }
    if (createdDateFilterParams.created_to) {
      filters.created_to = createdDateFilterParams.created_to;
    }

    return filters;
  }, [statesFilter, selectableStates, schedulingStatus, customerStatus, invoiceStatus, partsOrderStatus, confirmationStatus, mechanicsFilter, manufacturersFilter, dateFilterParams, createdDateFilterParams]);

  // Use server-side pagination hook
  const {
    data: maintenances,
    loading,
    error,
    page,
    pageSize,
    totalCount,
    totalPages,
    setPage,
    setPageSize,
    searchQuery,
    setSearchQuery,
    sortField,
    sortDirection,
    handleSort,
    refetch,
    setFilters,
  } = usePaginatedList('/api/maintenances/', {
    initialFilters: serverFilters,
    // Seed the sort from saved preferences. usePaginatedList only reads these on mount, and
    // useListPreferences loads from localStorage synchronously, so the saved sort is applied
    // to the very first request — no flash of the default ordering.
    defaultSortField: preferences.sortField || 'created_at',
    defaultSortDirection: preferences.sortDirection || 'desc',
    defaultPageSize: 50,
    initialSearchQuery: preferences.searchQuery || '',
  });

  // Update server filters when local filters change
  // Note: Page reset is handled automatically by usePaginatedList's effectivePage logic
  React.useEffect(() => {
    setFilters(serverFilters);
  }, [serverFilters, setFilters]);

  // Sync search query from localStorage preferences to hook
  React.useEffect(() => {
    setSearchQuery(preferences.searchQuery || '');
  }, [preferences.searchQuery, setSearchQuery]);

  // Persist the sort so it survives navigation, the same way the filters do. Mirroring the
  // hook's state (rather than wrapping handleSort) keeps the asc/desc toggle logic in one place.
  React.useEffect(() => {
    if (sortField !== preferences.sortField || sortDirection !== preferences.sortDirection) {
      updatePreferences({ sortField, sortDirection });
    }
  }, [sortField, sortDirection, preferences.sortField, preferences.sortDirection, updatePreferences]);

  // Get unique manufacturers from loaded data (for the filter dropdown)
  const uniqueManufacturers = useMemo(() => {
    const manufacturers = new Set();
    maintenances.forEach((m) => {
      if (m.vehicle?.manufacturer) {
        manufacturers.add(m.vehicle.manufacturer);
      }
    });
    return Array.from(manufacturers).sort();
  }, [maintenances]);

  const openMaintenanceDetails = (id) => {
    navigate(`/maintenances/${id}`);
  };

  const addCreatedMaintenance = () => {
    refetch();
  };

  const handleSwitchToAi = useCallback((data) => {
    setAiPreFillData(data);
    setAddModalOpen(false);
    setAiDialogOpen(true);
  }, []);

  const handleDeleteMaintenance = async (maintenanceId) => {
    try {
      await axios.delete(`/api/maintenances/${maintenanceId}/`);
      refetch();
    } catch (error) {
      console.error('Error deleting maintenance:', error);
    }
  };

  // Menu handlers
  const handleMenuOpen = (event, maintenance) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setMenuMaintenance(maintenance);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setMenuMaintenance(null);
  };

  const handleOpenCancelDialog = () => {
    setMaintenanceToCancel(menuMaintenance);
    setCancelModalOpen(true);
    handleMenuClose();
  };

  const handleConfirmCancel = async () => {
    if (!maintenanceToCancel) return;
    try {
      await axios.patch(`/api/maintenances/${maintenanceToCancel.id}/set_state/`, { state: 'cancelled' });
      refetch();
    } catch (error) {
      console.error('Error cancelling maintenance:', error);
    }
  };

  const handleViewInvoice = () => {
    if (menuMaintenance?.invoices?.length > 0) {
      const inv = menuMaintenance.invoices[0];
      if (inv.status === 'combined' && inv.combined_into) {
        navigate(`/invoices/${inv.combined_into}`);
      } else {
        navigate(`/invoices/${inv.id}`);
      }
    }
    handleMenuClose();
  };

  // Count active filters
  const activeFilterCount = useMemo(() => {
    let count = 0;
    const statesChanged = statesFilter.length !== defaultStates.length ||
      !statesFilter.every((s) => defaultStates.includes(s));
    if (statesChanged) count++;
    if (schedulingStatus !== 'all') count++;
    if (customerStatus !== 'all') count++;
    if (invoiceStatus !== 'all') count++;
    if (partsOrderStatus !== 'all') count++;
    if (confirmationStatus !== 'all') count++;
    if (mechanicsFilter.length > 0) count++;
    if (manufacturersFilter.length > 0) count++;
    if (dateRangeType !== 'all') count++;
    if (createdDateRangeType !== 'all') count++;
    return count;
  }, [statesFilter, defaultStates, schedulingStatus, customerStatus, invoiceStatus, partsOrderStatus, confirmationStatus, mechanicsFilter, manufacturersFilter, dateRangeType, createdDateRangeType]);

  // Build filter summary chips
  const filterSummaryChips = useMemo(() => {
    const chips = [];

    const statesChanged = statesFilter.length !== defaultStates.length ||
      !statesFilter.every((s) => defaultStates.includes(s));
    if (statesChanged) {
      if (statesFilter.length === 0) {
        chips.push({ key: 'states', label: t('No states selected') });
      } else if (statesFilter.length <= 2) {
        chips.push({ key: 'states', label: statesFilter.map((s) => t(s)).join(', ') });
      } else {
        chips.push({ key: 'states', label: t('{{count}} states', { count: statesFilter.length }) });
      }
    }

    if (schedulingStatus === 'scheduled') {
      chips.push({ key: 'scheduling', label: t('Scheduled') });
    } else if (schedulingStatus === 'unscheduled') {
      chips.push({ key: 'scheduling', label: t('Not scheduled') });
    }

    if (customerStatus === 'assigned') {
      chips.push({ key: 'customer', label: t('Has customer') });
    } else if (customerStatus === 'unassigned') {
      chips.push({ key: 'customer', label: t('No customer') });
    }

    if (invoiceStatus === 'has_invoice') {
      chips.push({ key: 'invoice', label: t('Has invoice') });
    } else if (invoiceStatus === 'no_invoice') {
      chips.push({ key: 'invoice', label: t('No invoice') });
    }

    if (partsOrderStatus !== 'all') {
      chips.push({ key: 'partsOrder', label: t(`parts_order.${partsOrderStatus}`) });
    }

    if (confirmationStatus !== 'all') {
      chips.push({ key: 'confirmation', label: t(`confirmation.filter_${confirmationStatus}`) });
    }

    if (mechanicsFilter.length > 0) {
      if (mechanicsFilter.length === 1) {
        const emp = employees.find((e) => e.id === mechanicsFilter[0]);
        chips.push({ key: 'mechanics', label: emp?.name || t('1 mechanic') });
      } else {
        chips.push({ key: 'mechanics', label: t('{{count}} mechanics', { count: mechanicsFilter.length }) });
      }
    }

    if (manufacturersFilter.length > 0) {
      if (manufacturersFilter.length === 1) {
        chips.push({ key: 'manufacturers', label: manufacturersFilter[0] });
      } else {
        chips.push({ key: 'manufacturers', label: t('{{count}} manufacturers', { count: manufacturersFilter.length }) });
      }
    }

    // Two date filters now, so each chip says which date it is about. Custom dates are shown
    // dd.MM.yyyy to match the pickers rather than the yyyy-MM-dd they are stored as.
    const asLocalDate = (value) => value.split('-').reverse().join('.');
    const pushDateChip = (key, prefix, rangeType, from, to) => {
      if (rangeType === 'today') {
        chips.push({ key, label: `${prefix}: ${t('Today')}` });
      } else if (rangeType === 'this_week') {
        chips.push({ key, label: `${prefix}: ${t('This week')}` });
      } else if (rangeType === 'this_month') {
        chips.push({ key, label: `${prefix}: ${t('This month')}` });
      } else if (rangeType === 'custom' && (from || to)) {
        if (from && to) {
          chips.push({ key, label: `${prefix}: ${asLocalDate(from)} – ${asLocalDate(to)}` });
        } else if (from) {
          chips.push({ key, label: `${prefix}: ${t('From')} ${asLocalDate(from)}` });
        } else {
          chips.push({ key, label: `${prefix}: ${t('To')} ${asLocalDate(to)}` });
        }
      }
    };

    pushDateChip('date', t('Scheduled Date'), dateRangeType, dateFrom, dateTo);
    pushDateChip('createdDate', t('Created Date'), createdDateRangeType, createdFrom, createdTo);

    return chips;
  }, [statesFilter, defaultStates, schedulingStatus, customerStatus, invoiceStatus, partsOrderStatus, confirmationStatus, mechanicsFilter, manufacturersFilter, dateRangeType, dateFrom, dateTo, createdDateRangeType, createdFrom, createdTo, employees, t]);

  // Reset all filters (resetLocalFilters also clears searchQuery in preferences)
  const resetFilters = useCallback(() => {
    resetLocalFilters();
  }, [resetLocalFilters]);

  // The state filter hides `cancelled` by default and persists per organization in
  // localStorage, so a search for a cancelled (or otherwise filtered-out) work order
  // silently returns nothing. Offer a way out that keeps the search term — plain
  // resetFilters() would clear it too.
  // Only widen to the states this view is allowed to show — the hint disappears once they are
  // all selected, because there is nothing left to widen to.
  const isStateFilterNarrowed = statesFilter.length < selectableStates.length;
  const showSearchAllStates =
    !loading && maintenances.length === 0 && !!searchQuery && isStateFilterNarrowed;

  const searchAllStates = useCallback(() => {
    updateFilters({ states: selectableStates.map((s) => s.value) });
  }, [updateFilters, selectableStates]);

  const emptySearchHint = showSearchAllStates ? (
    <Box sx={{ textAlign: 'center', mt: 2, mb: 1 }} data-testid="search-all-states-hint">
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        {t('Some states are hidden by your filters.')}
      </Typography>
      <Button variant="outlined" size="small" onClick={searchAllStates} data-testid="search-all-states">
        {t('Search all states')}
      </Button>
    </Box>
  ) : null;

  // State for error snackbar - cleared when error changes to null
  const [showErrorSnackbar, setShowErrorSnackbar] = React.useState(false);
  const prevError = React.useRef(error);

  React.useEffect(() => {
    // Show snackbar when a new error appears
    if (error && error !== prevError.current) {
      setShowErrorSnackbar(true);
    }
    prevError.current = error;
  }, [error]);

  const handleCloseErrorSnackbar = () => {
    setShowErrorSnackbar(false);
  };

  // Combine invoices from selected maintenances
  const [listSnackbar, setListSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const selectedMaintenancesList = useMemo(() => {
    if (!maintenances || selectedMaintenanceIds.size === 0) return [];
    return maintenances.filter((m) => selectedMaintenanceIds.has(m.id));
  }, [maintenances, selectedMaintenanceIds]);

  const canCombineFromMaintenances = useMemo(() => {
    if (selectedMaintenancesList.length < 2) return { enabled: false, reason: '' };
    const completedStates = ['completed', 'picked_up'];
    const allCompleted = selectedMaintenancesList.every((m) => completedStates.includes(m.state));
    if (!allCompleted) return { enabled: false, reason: t('Only completed work orders can be combined') };
    const firstCustomerId = selectedMaintenancesList[0].customer?.id;
    if (!firstCustomerId) return { enabled: false, reason: t('Work order has no customer') };
    const sameCustomer = selectedMaintenancesList.every((m) => m.customer?.id === firstCustomerId);
    if (!sameCustomer) return { enabled: false, reason: t('Combined invoice can only be created from work orders of the same customer') };
    const hasAlreadyCombined = selectedMaintenancesList.some((m) =>
      m.invoices?.some((inv) => inv.status === 'combined')
    );
    if (hasAlreadyCombined) return { enabled: false, reason: t('Work order already has a combined invoice') };
    const hasSentInvoice = selectedMaintenancesList.some((m) =>
      m.invoices?.some((inv) => ['sent', 'paid'].includes(inv.status))
    );
    if (hasSentInvoice) return { enabled: false, reason: t('Work order already has a sent invoice') };
    return { enabled: true, reason: '' };
  }, [selectedMaintenancesList, t]);

  const handleBulkPartsOrderStatus = async (newStatus) => {
    setPartsOrderMenuAnchor(null);
    try {
      await axios.post('/api/maintenances/bulk-set-parts-order-status/', {
        maintenance_ids: Array.from(selectedMaintenanceIds),
        status: newStatus,
      });
      setSelectedMaintenanceIds(new Set());
      refetch();
    } catch (err) {
      console.error('Bulk parts order status update failed:', err);
    }
  };

  const maintenanceSelectionActions = useMemo(() => {
    if (selectedMaintenanceIds.size === 0) return [];
    // Cancelling the selected quotes is how a workshop clears the years of old cost estimates a
    // migration brought in as quotes. Quotes are never invoiced and their parts aren't ordered
    // yet, so in the quotes view it is the only bulk action. Quotes live in the work orders
    // list by default (the "Quotes in their own list" option is off), so that list offers the
    // same action once every selected row is a quote; a mixed selection keeps the work-order
    // actions only. The server skips anything that is no longer a quote either way.
    const cancelQuotesAction = {
      label: t('Cancel selected quotes'),
      icon: <CancelIcon fontSize="small" />,
      onClick: () => setBulkCancelDialogOpen(true),
      color: 'error',
      variant: 'outlined',
    };
    if (quotesView) return [cancelQuotesAction];
    const actions = [];
    const onlyQuotesSelected =
      selectedMaintenancesList.length > 0 && selectedMaintenancesList.every((m) => m.state === 'quote');
    if (onlyQuotesSelected) actions.push(cancelQuotesAction);
    if (currentOrganization?.parts_order_tracking_enabled && selectedMaintenanceIds.size >= 1) {
      actions.push({
        label: t('parts_order.setStatus'),
        icon: <LocalShippingIcon fontSize="small" />,
        onClick: (e) => setPartsOrderMenuAnchor(e.currentTarget),
        variant: 'outlined',
      });
    }
    if (selectedMaintenanceIds.size >= 2) {
      actions.push({
        label: t('Create combined invoice from work orders'),
        icon: <MergeIcon fontSize="small" />,
        onClick: () => setCombineDialogOpen(true),
        disabled: !canCombineFromMaintenances.enabled,
        tooltip: canCombineFromMaintenances.reason || t('Select completed work orders from the same customer to create a combined invoice'),
        variant: 'contained',
      });
    }
    return actions;
  }, [selectedMaintenanceIds.size, selectedMaintenancesList, quotesView, canCombineFromMaintenances.enabled, currentOrganization?.parts_order_tracking_enabled, t]);

  const handleCombineFromMaintenances = async () => {
    setCombineLoading(true);
    try {
      const response = await axios.post('/api/invoices/combine-from-maintenances/', {
        maintenance_ids: Array.from(selectedMaintenanceIds),
      });
      setCombineDialogOpen(false);
      setSelectedMaintenanceIds(new Set());
      setListSnackbar({ open: true, message: t('Combined invoice created'), severity: 'success' });
      navigate(`/invoices/${response.data.id}`);
    } catch (err) {
      const msg = err.response?.data?.error || t('Error creating combined invoice');
      setListSnackbar({ open: true, message: msg, severity: 'error' });
    } finally {
      setCombineLoading(false);
    }
  };

  // Quotes view: cancel every selected quote. The server only touches rows still in the quote
  // state and reports the rest back as skipped, so a stale page cannot cancel started work.
  const handleBulkCancelQuotes = async () => {
    setBulkCancelLoading(true);
    try {
      const response = await axios.post('/api/maintenances/bulk-cancel-quotes/', {
        maintenance_ids: Array.from(selectedMaintenanceIds),
      });
      const { cancelled_count: cancelled = 0, skipped_ids: skipped = [] } = response.data || {};
      setBulkCancelDialogOpen(false);
      setSelectedMaintenanceIds(new Set());
      refetch();
      const parts = [t('quotesCancelled', { count: cancelled })];
      if (skipped.length > 0) parts.push(t('quotesSkipped', { count: skipped.length }));
      setListSnackbar({ open: true, message: parts.join(' '), severity: skipped.length > 0 ? 'warning' : 'success' });
    } catch (err) {
      console.error('Bulk cancel quotes failed:', err);
      setListSnackbar({ open: true, message: t('Cancelling quotes failed'), severity: 'error' });
    } finally {
      setBulkCancelLoading(false);
    }
  };

  return (
    <Box data-testid={quotesView ? 'quotes-list-page' : 'maintenance-list-page'}>
      {/* Error Snackbar - shows error without blocking the UI */}
      <Snackbar
        open={showErrorSnackbar && !!error}
        autoHideDuration={6000}
        onClose={handleCloseErrorSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseErrorSnackbar} severity="error" sx={{ width: '100%' }}>
          {t('Error')}: {error}
        </Alert>
      </Snackbar>
      {/* Header - Responsive */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', sm: 'center' },
          gap: 2,
          mb: 3,
        }}
      >
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            color: 'text.primary',
            fontFamily: "'Plus Jakarta Sans', sans-serif",
          }}
        >
          {t(quotesView ? 'nav.quotes' : 'Maintenances')}
        </Typography>
        {!simplifiedUi && (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            {currentOrganization?.quote_machine_enabled && can(currentOrganization, 'work_orders.create') && (
              <Button
                variant="outlined"
                data-testid="quote-machine-button"
                onClick={() => setQuoteMachineOpen(true)}
                startIcon={<BoltIcon />}
                sx={{
                  px: 2.5,
                  py: 1.5,
                  borderRadius: '10px',
                  fontWeight: 500,
                  fontSize: '14px',
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  borderColor: 'secondary.main',
                  color: 'secondary.main',
                  textTransform: 'none',
                  '&:hover': {
                    bgcolor: 'rgba(49, 130, 206, 0.08)',
                    borderColor: 'secondary.main',
                  },
                }}
              >
                {t('quoteMachine.button')}
              </Button>
            )}
            {currentOrganization?.oem_service_enabled && (
              <Button
                variant="outlined"
                onClick={() => setOemDialogOpen(true)}
                sx={{
                  px: 2.5,
                  py: 1.5,
                  borderRadius: '10px',
                  fontWeight: 500,
                  fontSize: '14px',
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  borderColor: 'secondary.main',
                  color: 'secondary.main',
                  textTransform: 'none',
                  '&:hover': {
                    bgcolor: 'rgba(49, 130, 206, 0.08)',
                    borderColor: 'secondary.main',
                  },
                }}
              >
                {t('oem.dialogTitle')}
              </Button>
            )}
            {can(currentOrganization, 'work_orders.create') && (
              <Button
                variant="contained"
                data-testid={quotesView ? 'add-quote-button' : 'add-maintenance-button'}
                onClick={() => {
                  setSelectedMaintenance(null);
                  setAddModalOpen(true);
                }}
                startIcon={<AddIcon />}
                sx={{
                  bgcolor: 'secondary.main',
                  color: 'white',
                  px: 2.5,
                  py: 1.5,
                  borderRadius: '10px',
                  fontWeight: 500,
                  fontSize: '14px',
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  boxShadow: '0 4px 12px rgba(49, 130, 206, 0.3)',
                  '&:hover': {
                    bgcolor: 'secondary.light',
                    boxShadow: '0 6px 16px rgba(49, 130, 206, 0.4)',
                  },
                }}
              >
                {t(quotesView ? 'New quote' : 'Add Maintenance')}
              </Button>
            )}
          </Box>
        )}
      </Box>

      {/*
        Employees only ever see maintenances assigned to them (MaintenanceViewSet.get_queryset),
        so searching for a colleague's work order returns nothing. Say so up front.
      */}
      {simplifiedUi && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
          {t('You only see maintenances assigned to you.')}
        </Typography>
      )}

      {/* Search and Filter Row - Responsive */}
      <Box sx={{ display: 'flex', gap: 1.5, mb: 2.5, flexDirection: { xs: 'column', sm: 'row' } }}>
        <SearchInput
          value={preferences.searchQuery || ''}
          onChange={(value) => updatePreferences({ searchQuery: value })}
          onClear={() => updatePreferences({ searchQuery: '' })}
          placeholder={t('Search by work number, registration, customer...')}
          data-testid="maintenance-search"
        />
        <Badge
          badgeContent={activeFilterCount}
          color="secondary"
          sx={{
            '& .MuiBadge-badge': {
              right: 12,
              top: 8,
            },
          }}
        >
          <Button
            variant={filterPanelOpen || activeFilterCount > 0 ? 'contained' : 'outlined'}
            startIcon={<FilterListIcon />}
            onClick={toggleFiltersOpen}
            sx={{
              px: 2,
              borderRadius: '10px',
              borderColor: filterPanelOpen || activeFilterCount > 0 ? 'secondary.main' : 'divider',
              bgcolor: filterPanelOpen || activeFilterCount > 0 ? 'secondary.main' : 'transparent',
              color: filterPanelOpen || activeFilterCount > 0 ? 'white' : 'text.primary',
              fontWeight: 500,
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              whiteSpace: 'nowrap',
              minWidth: { xs: '100%', sm: 'auto' },
              '&:hover': {
                borderColor: 'secondary.main',
                bgcolor: filterPanelOpen || activeFilterCount > 0 ? 'secondary.light' : 'background.paper',
              },
            }}
          >
            {t('Filter')}
          </Button>
        </Badge>
      </Box>

      {/* Active Filter Summary */}
      {filterSummaryChips.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            mb: 2,
            flexWrap: 'wrap',
          }}
        >
          <Typography
            sx={{
              fontSize: '13px',
              color: 'text.secondary',
              fontWeight: 500,
            }}
          >
            {t('Filtered')}:
          </Typography>
          {filterSummaryChips.map((chip) => (
            <Chip
              key={chip.key}
              label={chip.label}
              size="small"
              variant="outlined"
              sx={{
                borderRadius: '6px',
                fontSize: '12px',
                height: 24,
                bgcolor: 'background.paper',
                borderColor: 'divider',
              }}
            />
          ))}
          <Button
            size="small"
            onClick={resetFilters}
            sx={{
              fontSize: '12px',
              color: 'text.secondary',
              minWidth: 'auto',
              px: 1,
              '&:hover': {
                color: 'secondary.main',
              },
            }}
          >
            {t('Clear')}
          </Button>
        </Box>
      )}

      {/* Filter Panel */}
      <Collapse in={filterPanelOpen}>
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 2.5,
            borderRadius: '12px',
            border: 1,
            borderColor: 'divider',
          }}
        >
          {/* Quick Filters Row */}
          <Box sx={{ mb: 3 }}>
            <Typography
              sx={{
                fontSize: '13px',
                fontWeight: 600,
                color: 'text.secondary',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                mb: 1.5,
              }}
            >
              {t('Quick Filters')}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {/* Scheduling Status */}
              <FilterChip
                icon={<EventIcon />}
                label={t('Scheduled')}
                active={schedulingStatus === 'scheduled'}
                onClick={() => updateFilters({ schedulingStatus: schedulingStatus === 'scheduled' ? 'all' : 'scheduled' })}
                color="primary"
              />
              <FilterChip
                icon={<EventBusyIcon />}
                label={t('Not scheduled')}
                active={schedulingStatus === 'unscheduled'}
                onClick={() => updateFilters({ schedulingStatus: schedulingStatus === 'unscheduled' ? 'all' : 'unscheduled' })}
                color="warning"
              />

              <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

              {/* Customer Status */}
              <FilterChip
                icon={<PersonIcon />}
                label={t('Has customer')}
                active={customerStatus === 'assigned'}
                onClick={() => updateFilters({ customerStatus: customerStatus === 'assigned' ? 'all' : 'assigned' })}
                color="success"
              />
              <FilterChip
                icon={<PersonOffIcon />}
                label={t('No customer')}
                active={customerStatus === 'unassigned'}
                onClick={() => updateFilters({ customerStatus: customerStatus === 'unassigned' ? 'all' : 'unassigned' })}
                color="error"
              />

              {/* Invoice Status — a quote is never invoiced, so not offered in the quotes view */}
              {!quotesView && (
                <>
                  <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

                  <FilterChip
                    icon={<ReceiptIcon />}
                    label={t('Has invoice')}
                    active={invoiceStatus === 'has_invoice'}
                    onClick={() => updateFilters({ invoiceStatus: invoiceStatus === 'has_invoice' ? 'all' : 'has_invoice' })}
                    color="info"
                  />
                  <FilterChip
                    icon={<ReceiptLongIcon />}
                    label={t('No invoice')}
                    active={invoiceStatus === 'no_invoice'}
                    onClick={() => updateFilters({ invoiceStatus: invoiceStatus === 'no_invoice' ? 'all' : 'no_invoice' })}
                    color="default"
                  />
                </>
              )}

              {/* Parts are ordered after a quote is accepted, so not offered in the quotes view */}
              {!quotesView && currentOrganization?.parts_order_tracking_enabled && (
                <>
                  <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
                  <FilterChip
                    icon={<LocalShippingIcon />}
                    label={t('parts_order.not_ordered')}
                    active={partsOrderStatus === 'not_ordered'}
                    onClick={() => updateFilters({ partsOrderStatus: partsOrderStatus === 'not_ordered' ? 'all' : 'not_ordered' })}
                    color="error"
                  />
                  <FilterChip
                    icon={<LocalShippingIcon />}
                    label={t('parts_order.ordered')}
                    active={partsOrderStatus === 'ordered'}
                    onClick={() => updateFilters({ partsOrderStatus: partsOrderStatus === 'ordered' ? 'all' : 'ordered' })}
                    color="warning"
                  />
                  <FilterChip
                    icon={<LocalShippingIcon />}
                    label={t('parts_order.arrived')}
                    active={partsOrderStatus === 'arrived'}
                    onClick={() => updateFilters({ partsOrderStatus: partsOrderStatus === 'arrived' ? 'all' : 'arrived' })}
                    color="success"
                  />
                </>
              )}

              {/* Customer approval: offered in the quotes view too, quotes are what customers accept */}
              <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
              <FilterChip
                icon={<HourglassEmptyIcon />}
                label={t('confirmation.filter_pending')}
                active={confirmationStatus === 'pending'}
                onClick={() => updateFilters({ confirmationStatus: confirmationStatus === 'pending' ? 'all' : 'pending' })}
                color="warning"
                data-testid="filter-confirmation-pending"
              />
              <FilterChip
                icon={<CheckCircleOutlineIcon />}
                label={t('confirmation.filter_confirmed')}
                active={confirmationStatus === 'confirmed'}
                onClick={() => updateFilters({ confirmationStatus: confirmationStatus === 'confirmed' ? 'all' : 'confirmed' })}
                color="success"
                data-testid="filter-confirmation-confirmed"
              />
              <FilterChip
                icon={<HighlightOffIcon />}
                label={t('confirmation.filter_declined')}
                active={confirmationStatus === 'declined'}
                onClick={() => updateFilters({ confirmationStatus: confirmationStatus === 'declined' ? 'all' : 'declined' })}
                color="error"
                data-testid="filter-confirmation-declined"
              />
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Scheduled Date Range */}
          <Box sx={{ mb: 3 }}>
            <Typography
              sx={{
                fontSize: '13px',
                fontWeight: 600,
                color: 'text.secondary',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                mb: 1.5,
              }}
            >
              {t('Scheduled Date')}
            </Typography>
            <DateRangeFilterRow
              rangeType={dateRangeType}
              from={dateFrom}
              to={dateTo}
              onChange={({ rangeType, from, to }) => updateFilters({
                ...(rangeType !== undefined && { dateRangeType: rangeType }),
                ...(from !== undefined && { dateFrom: from }),
                ...(to !== undefined && { dateTo: to }),
              })}
              testIdPrefix="scheduled-date"
              t={t}
            />
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Created Date Range — same controls, filtering on created_at instead */}
          <Box sx={{ mb: 3 }}>
            <Typography
              sx={{
                fontSize: '13px',
                fontWeight: 600,
                color: 'text.secondary',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                mb: 1.5,
              }}
            >
              {t('Created Date')}
            </Typography>
            <DateRangeFilterRow
              rangeType={createdDateRangeType}
              from={createdFrom}
              to={createdTo}
              onChange={({ rangeType, from, to }) => updateFilters({
                ...(rangeType !== undefined && { createdDateRangeType: rangeType }),
                ...(from !== undefined && { createdFrom: from }),
                ...(to !== undefined && { createdTo: to }),
              })}
              testIdPrefix="created-date"
              t={t}
            />
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Dropdown Filters */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            {/* States Filter — the quotes list is single-state by definition, so it has none. */}
            {!quotesView && (
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>{t('State')}</InputLabel>
                <Select
                  multiple
                  value={statesFilter}
                  onChange={(e) => updateFilters({ states: e.target.value })}
                  label={t('State')}
                  renderValue={(selected) => {
                    if (selected.length === selectableStates.length) return t('All');
                    if (selected.length === 0) return t('None');
                    return selected.map((s) => t(s)).join(', ');
                  }}
                >
                  {selectableStates.map((state) => (
                    <MenuItem key={state.value} value={state.value}>
                      <Checkbox checked={statesFilter.includes(state.value)} />
                      <ListItemText primary={t(state.labelKey)} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {/* Mechanics Filter */}
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>{t('Mechanics')}</InputLabel>
              <Select
                multiple
                value={mechanicsFilter}
                onChange={(e) => updateFilters({ mechanics: e.target.value })}
                label={t('Mechanics')}
                renderValue={(selected) => {
                  if (selected.length === 0) return '';
                  const names = selected.map((id) => {
                    const emp = employees.find((e) => e.id === id);
                    return emp?.name || '';
                  });
                  return names.join(', ');
                }}
              >
                {employees.map((employee) => (
                  <MenuItem key={employee.id} value={employee.id}>
                    <Checkbox checked={mechanicsFilter.includes(employee.id)} />
                    <ListItemText primary={employee.name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Manufacturer Filter */}
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>{t('Manufacturer')}</InputLabel>
              <Select
                multiple
                value={manufacturersFilter}
                onChange={(e) => updateFilters({ manufacturers: e.target.value })}
                label={t('Manufacturer')}
                renderValue={(selected) => selected.join(', ')}
              >
                {uniqueManufacturers.map((manufacturer) => (
                  <MenuItem key={manufacturer} value={manufacturer}>
                    <Checkbox checked={manufacturersFilter.includes(manufacturer)} />
                    <ListItemText primary={manufacturer} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Actions Row */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3 }}>
            <Typography variant="body2" color="text.secondary">
              {quotesView
                ? t('quotesList.showingCount', { count: totalCount })
                : t('Showing {{count}} maintenances', { count: totalCount })}
            </Typography>
            <Button
              size="small"
              startIcon={<RestartAltIcon />}
              onClick={resetFilters}
              disabled={activeFilterCount === 0 && !searchQuery}
              sx={{ color: 'text.secondary' }}
            >
              {t('Reset filters')}
            </Button>
          </Box>
        </Paper>
      </Collapse>

      {/* Desktop Table */}
      <DataTable
        columns={[
          {
            key: 'work_number',
            label: t('Work #'),
            sortable: true,
            width: '1%',
            noWrap: true,
            render: (maintenance) => (
              <Typography sx={{ fontSize: '13px', fontWeight: 600, color: 'text.secondary', ...numFont }}>
                {maintenance.work_number || '-'}
              </Typography>
            ),
          },
          {
            key: 'customer',
            label: `${t('Customer')} & ${t('Description')}`,
            sortable: true,
            sortField: 'customer__name',
            maxWidth: 250,
            render: (maintenance) => (
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  sx={{
                    fontSize: '14px',
                    fontWeight: 500,
                    color: 'text.primary',
                    mb: 0.25,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {customerLabel(maintenance.customer) || t('No customer')}
                </Typography>
                <Typography
                  sx={{
                    fontSize: '13px',
                    color: 'text.secondary',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {maintenance.description}
                </Typography>
              </Box>
            ),
          },
          {
            key: 'registration',
            label: t('Reg. number'),
            sortable: true,
            sortField: 'vehicle__registration_number',
            width: '1%',
            noWrap: true,
            render: (maintenance) => (
              <Typography sx={{ fontSize: '14px', fontWeight: 600, color: 'primary.main', ...plateFont }}>
                {maintenance.vehicle?.registration_number}
              </Typography>
            ),
          },
          {
            key: 'vehicle',
            label: t('Vehicle'),
            sortable: true,
            sortField: 'vehicle__manufacturer',
            noWrap: true,
            maxWidth: 130,
            hideBelow: 'xl',
            render: (maintenance) => {
              const text = [maintenance.vehicle?.manufacturer, maintenance.vehicle?.model, maintenance.vehicle?.year_model].filter(Boolean).join(' ');
              return (
                <Typography sx={{ fontSize: '13px', color: 'text.secondary', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {text}
                </Typography>
              );
            },
          },
          {
            key: 'state',
            label: t('State'),
            width: '1%',
            noWrap: true,
            render: (maintenance) => (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'flex-start' }}>
                <StatusBadge status={maintenance.state} detail={maintenance.waiting_reason ? t(`waiting_reason.${maintenance.waiting_reason}`) : undefined} />
                {!quotesView && currentOrganization?.parts_order_tracking_enabled && maintenance.parts_order_status && (
                  <PartsOrderBadge status={maintenance.parts_order_status} t={t} />
                )}
                <ConfirmationBadge maintenance={maintenance} t={t} />
                <ExtraWorkBadge maintenance={maintenance} t={t} />
              </Box>
            ),
          },
          {
            key: 'scheduled',
            label: t('Scheduled'),
            sortable: true,
            sortField: 'earliest_schedule',
            width: '1%',
            noWrap: true,
            hideBelow: 'lg',
            render: (maintenance) => (
              <Typography sx={{ fontSize: '13px', color: maintenance.schedules?.[0] ? 'text.secondary' : 'text.disabled' }}>
                {maintenance.schedules?.[0]
                  ? formatDate(maintenance.schedules[0].start_time)?.split(' ')[0]
                  : '-'}
              </Typography>
            ),
          },
          {
            key: 'created_at',
            label: t('Created'),
            sortable: true,
            width: '1%',
            noWrap: true,
            hideBelow: 'xl',
            render: (maintenance) => (
              <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>
                {maintenance.created_at
                  ? formatDate(maintenance.created_at)?.split(' ')[0]
                  : '-'}
              </Typography>
            ),
          },
          // A quote is never invoiced — the column would be a full row of dashes.
          ...(quotesView ? [] : [{
            key: 'invoiced',
            label: t('Invoiced'),
            width: '1%',
            noWrap: true,
            hideBelow: 'xl',
            render: (maintenance) =>
              maintenance.invoices?.length > 0 ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'flex-start' }}>
                  {maintenance.invoices.map((inv) => (
                    <Chip
                      key={inv.id}
                      size="small"
                      clickable={inv.status === 'combined' && inv.combined_into}
                      onClick={inv.status === 'combined' && inv.combined_into
                        ? (e) => { e.stopPropagation(); navigate(`/invoices/${inv.combined_into}`); }
                        : undefined}
                      label={inv.status === 'combined'
                        ? t('Combined')
                        : inv.is_overdue
                          ? t('Overdue')
                          : t(inv.status.charAt(0).toUpperCase() + inv.status.slice(1))}
                      sx={{
                        fontSize: '11px',
                        height: 22,
                        bgcolor: inv.status === 'paid'
                          ? 'rgba(16, 185, 129, 0.1)'
                          : inv.status === 'combined'
                            ? 'rgba(156, 163, 175, 0.15)'
                            : inv.is_overdue
                              ? 'rgba(239, 68, 68, 0.1)'
                              : 'rgba(49, 130, 206, 0.1)',
                        color: inv.status === 'paid'
                          ? 'success.main'
                          : inv.status === 'combined'
                            ? 'text.secondary'
                            : inv.is_overdue
                              ? 'error.main'
                              : 'secondary.main',
                        fontWeight: 500,
                      }}
                    />
                  ))}
                </Box>
              ) : (
                <Typography sx={{ fontSize: '13px', color: 'text.disabled' }}>-</Typography>
              ),
          }]),
          {
            key: 'actions',
            label: '',
            width: '1%',
            render: (maintenance) => (
              <IconButton
                onClick={(e) => handleMenuOpen(e, maintenance)}
                sx={{ color: 'text.disabled', '&:hover': { color: 'text.primary' } }}
              >
                <MoreVertIcon fontSize="small" />
              </IconButton>
            ),
          },
        ]}
        data={maintenances}
        loading={loading}
        onRowClick={(maintenance) => openMaintenanceDetails(maintenance.id)}
        sortField={sortField}
        sortDirection={sortDirection}
        onSort={handleSort}
        emptyTitle={searchQuery || activeFilterCount > 0
          ? t(quotesView ? 'quotesList.noQuotesMatch' : 'No maintenances match your criteria')
          : t(quotesView ? 'quotesList.noQuotesYet' : 'No maintenances yet')}
        // Selection is on in both views: the work orders list offers parts-order status and the
        // combined invoice, the quotes view offers cancelling the selected quotes.
        selectable
        selectedIds={selectedMaintenanceIds}
        onSelectionChange={setSelectedMaintenanceIds}
        selectionActions={maintenanceSelectionActions}
        testIdPrefix="maintenance-row"
        tableTestId="maintenances-table"
        pagination={{
          page,
          totalPages,
          totalCount,
          pageSize,
          onPageChange: setPage,
          onPageSizeChange: setPageSize,
        }}
      />
      {/* DataTable has no action slot in its empty state, so the escape hatch sits below it */}
      <Box sx={{ display: { xs: 'none', lg: 'block' } }}>{emptySearchHint}</Box>

      {/* Mobile Cards - Hidden on desktop */}
      <Box sx={{ display: { xs: 'block', lg: 'none' }, position: 'relative' }}>
        {/* Loading overlay for refetch */}
        {loading && maintenances.length > 0 && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              bgcolor: 'rgba(255, 255, 255, 0.7)',
              zIndex: 1,
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'center',
              pt: 8,
            }}
          >
            <CircularProgress size={32} />
          </Box>
        )}
        {loading && maintenances.length === 0 ? (
          <Box sx={{ p: 6, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress size={32} />
          </Box>
        ) : maintenances.length === 0 ? (
          <Paper elevation={0} sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">
              {searchQuery || activeFilterCount > 0
                ? t(quotesView ? 'quotesList.noQuotesMatch' : 'No maintenances match your criteria')
                : t(quotesView ? 'quotesList.noQuotesYet' : 'No maintenances yet')}
            </Typography>
            {emptySearchHint}
          </Paper>
        ) : (
          <>
            {maintenances.map((maintenance) => (
              <Paper
                key={maintenance.id}
                data-testid={`maintenance-card-${maintenance.id}`}
                elevation={0}
                onClick={() => openMaintenanceDetails(maintenance.id)}
                sx={{
                  p: 2,
                  mb: 1.5,
                  cursor: 'pointer',
                  transition: 'background-color 0.15s ease',
                  '&:hover': {
                    bgcolor: 'action.hover',
                  },
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                  <Box sx={{ display: 'flex', gap: 1.5, flex: 1, minWidth: 0 }}>
                    {/* Vehicle Icon */}
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '10px',
                        bgcolor: 'rgba(49, 130, 206, 0.1)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'secondary.main',
                        flexShrink: 0,
                      }}
                    >
                      <DirectionsCarIcon />
                    </Box>

                    {/* Main Info */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                        {maintenance.work_number && (
                          <Typography
                            sx={{
                              fontSize: '12px',
                              fontWeight: 600,
                              color: 'text.secondary',
                              ...numFont,
                            }}
                          >
                            #{maintenance.work_number}
                          </Typography>
                        )}
                        <Typography
                          sx={{
                            fontSize: '15px',
                            fontWeight: 600,
                            color: 'primary.main',
                            ...plateFont,
                          }}
                        >
                          {maintenance.vehicle?.registration_number}
                        </Typography>
                        <StatusBadge status={maintenance.state} detail={maintenance.waiting_reason ? t(`waiting_reason.${maintenance.waiting_reason}`) : undefined} />
                        {!quotesView && currentOrganization?.parts_order_tracking_enabled && maintenance.parts_order_status && (
                          <PartsOrderBadge status={maintenance.parts_order_status} t={t} />
                        )}
                        <ConfirmationBadge maintenance={maintenance} t={t} />
                        <ExtraWorkBadge maintenance={maintenance} t={t} />
                        {!quotesView && maintenance.invoices?.map((inv) => (
                          <Chip
                            key={inv.id}
                            size="small"
                            label={inv.status === 'combined'
                              ? t('Combined')
                              : inv.is_overdue
                                ? t('Overdue')
                                : t(inv.status.charAt(0).toUpperCase() + inv.status.slice(1))}
                            sx={{
                              fontSize: '10px',
                              height: 20,
                              bgcolor: inv.status === 'paid'
                                ? 'rgba(16, 185, 129, 0.1)'
                                : inv.status === 'combined'
                                  ? 'rgba(156, 163, 175, 0.15)'
                                  : inv.is_overdue
                                    ? 'rgba(239, 68, 68, 0.1)'
                                    : 'rgba(49, 130, 206, 0.1)',
                              color: inv.status === 'paid'
                                ? 'success.main'
                                : inv.status === 'combined'
                                  ? 'text.secondary'
                                  : inv.is_overdue
                                    ? 'error.main'
                                    : 'secondary.main',
                              fontWeight: 500,
                            }}
                          />
                        ))}
                      </Box>
                      <Typography
                        sx={{
                          fontSize: '14px',
                          fontWeight: 500,
                          color: 'text.primary',
                          mb: 0.25,
                        }}
                      >
                        {customerLabel(maintenance.customer) || t('No customer')}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: '13px',
                          color: 'text.secondary',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {maintenance.vehicle?.manufacturer} {maintenance.vehicle?.model}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Actions */}
                  <IconButton
                    onClick={(e) => handleMenuOpen(e, maintenance)}
                    sx={{
                      color: 'text.disabled',
                      '&:hover': {
                        color: 'text.primary',
                      },
                    }}
                  >
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                </Box>

                {/* Description and Schedule */}
                <Box sx={{ pl: 7 }}>
                  {maintenance.description && (
                    <Typography
                      sx={{
                        fontSize: '13px',
                        color: 'text.secondary',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        mb: 0.5,
                      }}
                    >
                      {maintenance.description}
                    </Typography>
                  )}
                  {maintenance.schedules?.[0] && (
                    <Typography
                      sx={{
                        fontSize: '12px',
                        color: 'text.disabled',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                      }}
                    >
                      <EventIcon sx={{ fontSize: 14 }} />
                      {formatDate(maintenance.schedules[0].start_time)?.split(' ')[0]}
                    </Typography>
                  )}
                </Box>
              </Paper>
            ))}

            {/* Mobile Pagination Controls */}
            <PaginationControls
              page={page}
              totalPages={totalPages}
              totalCount={totalCount}
              pageSize={pageSize}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
              loading={loading}
            />
          </>
        )}
      </Box>

      <AddMaintenanceDialog
        open={addModalOpen}
        addCreatedMaintenance={addCreatedMaintenance}
        handleClose={() => setAddModalOpen(false)}
        // Create a quote from the quotes list — a planned work order would vanish from the
        // list the moment it was created.
        initialState={quotesView ? 'quote' : 'planned'}
        onSwitchToAi={currentOrganization?.ai_work_order_enabled ? handleSwitchToAi : undefined}
      />
      <OemServiceDialog
        open={oemDialogOpen}
        handleClose={() => setOemDialogOpen(false)}
        mode="create"
        addCreatedMaintenance={addCreatedMaintenance}
      />
      {currentOrganization?.quote_machine_enabled && (
        <QuoteMachineDialog
          open={quoteMachineOpen}
          handleClose={() => setQuoteMachineOpen(false)}
          addCreatedMaintenance={addCreatedMaintenance}
        />
      )}
      <AIWorkOrderDialog
        open={aiDialogOpen}
        handleClose={() => { setAiDialogOpen(false); setAiPreFillData(null); }}
        addCreatedMaintenance={addCreatedMaintenance}
        vehicle={aiPreFillData?.vehicle}
        customer={aiPreFillData?.customer}
        initialText={aiPreFillData?.text}
      />
      <DeleteMaintenanceDialog
        open={deleteModalOpen}
        handleClose={() => setDeleteModalOpen(false)}
        onDelete={handleDeleteMaintenance}
        maintenanceId={selectedMaintenance}
      />
      <CancelMaintenanceDialog
        open={cancelModalOpen}
        handleClose={() => setCancelModalOpen(false)}
        onConfirm={handleConfirmCancel}
      />

      {/* Combine from Maintenances Dialog */}
      <Dialog
        open={combineDialogOpen}
        onClose={() => !combineLoading && setCombineDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t('Create combined invoice?')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('Creating combined invoice from {{count}} work orders. Continue?', { count: selectedMaintenanceIds.size })}
          </DialogContentText>
          {!canCombineFromMaintenances.enabled && canCombineFromMaintenances.reason && (
            <Alert severity="warning" sx={{ mt: 2 }}>{canCombineFromMaintenances.reason}</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCombineDialogOpen(false)} disabled={combineLoading}>
            {t('No, go back')}
          </Button>
          <Button
            variant="contained"
            onClick={handleCombineFromMaintenances}
            disabled={combineLoading || !canCombineFromMaintenances.enabled}
          >
            {combineLoading ? <CircularProgress size={24} /> : t('Yes, create combined invoice')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk cancel quotes confirmation (quotes view) */}
      <Dialog
        open={bulkCancelDialogOpen}
        onClose={() => !bulkCancelLoading && setBulkCancelDialogOpen(false)}
        data-testid="bulk-cancel-quotes-dialog"
      >
        <DialogTitle>{t('Cancel selected quotes?')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t('quotesBulkCancelBody', { count: selectedMaintenanceIds.size })}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkCancelDialogOpen(false)} disabled={bulkCancelLoading}>
            {t('Cancel')}
          </Button>
          <Button
            onClick={handleBulkCancelQuotes}
            color="error"
            variant="contained"
            disabled={bulkCancelLoading}
            data-testid="bulk-cancel-quotes-confirm"
          >
            {bulkCancelLoading ? <CircularProgress size={24} /> : t('Cancel selected quotes')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* List Snackbar: combined invoice and bulk cancel outcomes */}
      <Snackbar
        open={listSnackbar.open}
        autoHideDuration={4000}
        onClose={() => setListSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={listSnackbar.severity} onClose={() => setListSnackbar((s) => ({ ...s, open: false }))}>
          {listSnackbar.message}
        </Alert>
      </Snackbar>

      {/* Parts Order Status Bulk Menu */}
      <Menu
        anchorEl={partsOrderMenuAnchor}
        open={Boolean(partsOrderMenuAnchor)}
        onClose={() => setPartsOrderMenuAnchor(null)}
      >
        {['not_ordered', 'ordered', 'arrived', 'not_needed'].map((s) => (
          <MenuItem key={s} onClick={() => handleBulkPartsOrderStatus(s)}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: PARTS_ORDER_CONFIG[s].color, mr: 1.5, flexShrink: 0 }} />
            {t(`parts_order.${s}_short`)}
          </MenuItem>
        ))}
      </Menu>

      {/* Actions Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            borderRadius: '10px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
            minWidth: 180,
          },
        }}
      >
        <MenuItem
          onClick={() => {
            if (menuMaintenance) {
              navigate(`/maintenances/${menuMaintenance.id}`);
            }
            handleMenuClose();
          }}
          sx={{ gap: 1.5 }}
        >
          <OpenInNewIcon fontSize="small" sx={{ color: 'text.secondary' }} />
          {t('Open maintenance')}
        </MenuItem>
        {menuMaintenance?.invoices?.length > 0 && (
          <MenuItem onClick={handleViewInvoice} sx={{ gap: 1.5 }}>
            <ReceiptIcon fontSize="small" sx={{ color: 'text.secondary' }} />
            {t('Show invoice')}
          </MenuItem>
        )}
        {menuMaintenance?.state !== 'cancelled' && menuMaintenance?.state !== 'picked_up' && (
          <MenuItem onClick={handleOpenCancelDialog} sx={{ gap: 1.5, color: 'error.main' }}>
            <CancelIcon fontSize="small" />
            {t('Cancel maintenance')}
          </MenuItem>
        )}
      </Menu>
    </Box>
  );
};

export default withTranslation()(MainMaintenancesView);
